import { Component, QueryList, ViewChildren } from '@angular/core';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { TranslationEntry } from '@app/shared/models/translatable';
import {
    CaseResult,
    CaseResultValues,
    ResultTypes,
    ResultValuationTaxTypes,
    ResultValues,
    TotalLossTypes,
    VatCalculationEnum
} from '@app/shared/models/case-result';
import { BasePatchComponent } from '@app/shared/components/base/base-patch.component';
import { ResultService, ResultsUriBuilderParams } from '@app/core/services/result/result.service';
import {
    AdditionsResultService,
    AdditionsResultsUriBuilderParams
} from '@app/core/services/result/additions-result.service';
import { OP, OpData} from '@app/core/services/base/base-service-with-bundle.service';
import {ResultsFurtherComponent} from '@app/modules/case/components/case-results/further/results-further.component';
import {MatLegacySelect as MatSelect} from '@angular/material/legacy-select';


@Component({
    selector: 'case-results-editable',
    templateUrl: './case-results-editable.component.html',
    styleUrls: ['./case-results-editable.component.scss']
})
export class CaseResultsEditableComponent extends BasePatchComponent<CaseResult, ResultsUriBuilderParams> {

    protected paramsIdNames = ['id'];
    protected myConfig = {fetchAtInit: true};

    @ViewChildren('furtherResultCmp')
    public furtherResultComponents: QueryList<ResultsFurtherComponent>;

    get result() {
        return this.updated;
    }

    get VatCalculationEnum() {
        return VatCalculationEnum;
    }

    get caseId() {
        return this.id;
    }

    public selectedTotalLossAssessment: TranslationEntry;
    public totalLossAssessmentValues: TranslationEntry[];

    public selectedValuationType: TranslationEntry;
    public valuationResultTypeValues: TranslationEntry[];

    public selectedValuationTaxType: TranslationEntry;
    public valuationResultTaxTypes: TranslationEntry[];

    public resultTypes: TranslationEntry[];
    public filteredResultTypes: TranslationEntry[];

    constructor(protected componentServiceBundle: ComponentServiceBundle,
                protected resultService: ResultService,
                protected additionsResultService: AdditionsResultService) {
        super(componentServiceBundle, resultService);
        this.totalLossAssessmentValues = TotalLossTypes.ALL;
        this.valuationResultTaxTypes = ResultValuationTaxTypes.ALL;
        this.valuationResultTypeValues = ResultTypes.VALUATION_RESULT_TYPES;
        this.resultTypes = ResultTypes.GENERAL_RESULT_TYPES;
    }

    protected whenOriginalFetched(): void {
        if (this.result) {
            this.selectedTotalLossAssessment = this.totalLossAssessmentValues.find(val => val.key === this.result.general.assessment_id);
            this.selectedValuationType = this.valuationResultTypeValues.find(val => val.key === this.result.valuation.result_type_id);
            this.selectedValuationTaxType = this.valuationResultTaxTypes.find(val => val.key === this.result.valuation.tax_type_id);

            this.refreshTypesList();
        }
    }

    public addFurtherResults(): void {
        if (this.filteredResultTypes.length !== 0 && this.result.additions.length < 8) {
            this.additionsResultService.create(new AdditionsResultsUriBuilderParams(this.caseId), new CaseResultValues())
                .subscribe(result => {
                    this.original.additions.unshift(result);
                    this.updated.additions.unshift(new CaseResultValues(result));

                    setTimeout(() => {
                        const foundComponent = this.furtherResultComponents.find(comp => comp.result.id === result.id);

                        if (foundComponent) {
                            foundComponent.mffResultType._elementRef.nativeElement.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start'
                            });

                            setTimeout(() => {
                                (<MatSelect>foundComponent.mffResultType._control).open();
                            }, 500);
                        }
                    });

                });
        }
    }

    public removeFurtherResult(toDelete: CaseResultValues): void {
        this.original.additions = this.result.additions.filter(res => res.id !== toDelete.id);
        this.updated.additions = this.result.additions.filter(res => res.id !== toDelete.id);
        this.refreshTypesList();
    }

    public updateFurtherResult(updated: CaseResultValues): void {
        this.updateFurtherResultIn(this.original, updated);
        this.updateFurtherResultIn(this.updated, updated);

        this.refreshTypesList();
    }

    protected updateFurtherResultIn(result: CaseResult, updated: CaseResultValues): void {
        const furtherResult = result.additions.find(res => res.id === updated.id);

        if (furtherResult) {
            furtherResult.result_type_id = updated.result_type_id;
            furtherResult.result_type = updated.result_type;
            furtherResult.net = updated.net;
            furtherResult.tax = updated.tax;
            furtherResult.gross = updated.gross;
            furtherResult.comment = updated.comment;
        }
    }

    public refreshTypesList(): void {
        this.filteredResultTypes = this.resultTypes.filter(type => !this.result.additions.find(res => res.result_type_id === type.key));
    }

    public updateAssessmentType(): void {
        this.patchComboValues('general/assessment', this.selectedTotalLossAssessment);
    }

    public updateValuationType(): void {
        this.patchComboValues('valuation/result_type', this.selectedValuationType);
    }

    public updateTaxationType(): void {
        this.patchComboValues('valuation/tax_type', this.selectedValuationTaxType);
    }

    private patchComboValues(transPath: string, selected: TranslationEntry): void {
        let opData: OpData[];

        // allow to store empty combos:
        if(!selected) {
            opData = [{
                op: OP.replace,
                path: `${transPath}_id`,
                value: ''
            }, {
                op: OP.replace,
                path: transPath,
                value: ''
            }];
        } else {
            opData = [{
                op: OP.replace,
                path: `${transPath}_id`,
                value: selected.key
            }, {
                op: OP.replace,
                path: transPath,
                value: this.translateService.instant(selected.trans)
            }];
        }

        this.patchOpDataArray(opData);
    }

    public calculate(path: string, from: string): void {
        const opData = [{
            op: OP.copy,
            path: this.convertToPatchPath(path),
            from: this.convertToPatchPath(from),
        }];

        this.patchOpDataArray(opData);
    }

    public getToolTip(taxType: VatCalculationEnum, net: number, gross: number): string {
        return ResultValues.getNetGrossCalculationTooltip(this.result.general.tax_percentage, taxType, net, gross);
    }

    protected generateUriBuilderParams(): ResultsUriBuilderParams {
        return new ResultsUriBuilderParams(this.caseId);
    }

}
